/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';

import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   JustifyContent,
} from '@dreryk/react-components';

import { compact } from '~libs.typescript';

import { receptionServiceTranslationKeys } from '../../../../translations';

import { ReceptionServiceInteractiveTableEstimatedDurationCell } from './cells/ReceptionServiceInteractiveTableEstimatedDurationCell';
import { ReceptionServiceInteractiveTablePriceRangeCell } from './cells/ReceptionServiceInteractiveTablePriceRangeCell';
import { ReceptionServiceInteractiveTableServiceCell } from './cells/ReceptionServiceInteractiveTableServiceCell';
import { ReceptionServiceInteractiveTableAdvancePaymentCell } from './cells/ReceptionServiceInteractiveTableAdvancePaymentCell';
import { ReceptionServiceTableBean } from './ReceptionServiceInteractiveTable.types';

export const useReceptionServiceInteractiveTableColumns = (
   onlinePaymentsIntegrationExists: boolean
): InteractiveTableColumnConfiguration<ReceptionServiceTableBean>[] => {
   return useMemo((): InteractiveTableColumnConfiguration<ReceptionServiceTableBean>[] => {
      const columnWidths = createDynamicColumnsWidth({
         controller: '0px',
         services: '100%',
         price: '190px',
         advancePayment: onlinePaymentsIntegrationExists ? '105px' : '0px',
         estimatedDuration: '105px',
         options: '24px',
      });

      return compact([
         {
            name: 'controller',
            width: columnWidths.controller,
            hidden: true,
            renderer: ({ bean, index }) => (
               <InteractiveTable.ControllerCell
                  name={'receptionService'}
                  testId={'receptionService'}
                  index={index}
                  defaultValue={bean.receptionService}
               />
            ),
         },
         {
            name: 'services',
            label: receptionServiceTranslationKeys.extensions.calendar.appointment.table.headers.services,
            width: columnWidths.services,
            renderer: ({ bean: { receptionService } }) => (
               <ReceptionServiceInteractiveTableServiceCell
                  name={receptionService.name}
                  additionalInformation={receptionService.additionalInformation}
                  color={receptionService.color}
               />
            ),
         },
         {
            name: 'price',
            label: receptionServiceTranslationKeys.extensions.calendar.appointment.table.headers.price,
            width: columnWidths.price,
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,
            renderer: ({ bean: { receptionService } }) => (
               <ReceptionServiceInteractiveTablePriceRangeCell
                  minPrice={receptionService.minPrice}
                  maxPrice={receptionService.maxPrice}
               />
            ),
         },
         onlinePaymentsIntegrationExists
            ? {
                 name: 'advancePayment',
                 label: receptionServiceTranslationKeys.extensions.calendar.appointment.table.headers.advancePayment,
                 width: columnWidths.advancePayment,
                 justifyContent: JustifyContent.END,
                 textAlign: HorizontalAlign.RIGHT,
                 renderer: ({ bean: { receptionService } }) => (
                    <ReceptionServiceInteractiveTableAdvancePaymentCell
                       advancePaymentValue={receptionService.advancePaymentValue}
                    />
                 ),
              }
            : undefined,
         {
            name: 'estimatedDuration',
            label: receptionServiceTranslationKeys.extensions.calendar.appointment.table.headers.estimatedDuration,
            width: columnWidths.estimatedDuration,
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,
            renderer: ({ bean: { receptionService } }) => (
               <ReceptionServiceInteractiveTableEstimatedDurationCell
                  estimatedDuration={receptionService.estimatedDuration}
               />
            ),
         },
         {
            name: 'options',
            width: columnWidths.options,
            renderer: ({ index }) => <InteractiveTable.DeleteRowCell index={index} />,
         },
      ]);
   }, [onlinePaymentsIntegrationExists]);
};
